import { find, first, isEmpty, last, orderBy } from 'lodash-es';
import { makeAutoObservable, observable } from 'mobx';
import { lowerCasedBlacklistedOptionValues } from '../../utils/constants';
import { isAvailableColorsMetafieldStale } from '../../utils/environment';
import { processBadgeDiscount, processBadgeUrgency } from '../product-listing-page/PlpUtils/ProcessBadgeDiscount';
import { ImagesEdge, ProductNode } from '../product-listing-page/types';
import { getCurveImages } from '../product/components/ProductImages/logic';
import { getProductImageParts } from '../product/utils';
import { AvailableColorMetaFieldData } from './types';

export function productDataGenerator(
  productData: ProductNode,
  options: { excludeSoldOut?: boolean; collectionHandle?: string }
) {
  const { excludeSoldOut = false, collectionHandle } = options;
  const schemaConfig = {
    activeSwatchIndex: 0,
    hasColorChanged: false,
    initialColor: '',
    productData,
    get availableColors(): AvailableColorMetaFieldData[] {
      const { availableColors: colorJson } = productData;
      const unsortedAvailableColors: AvailableColorMetaFieldData[] =
        isAvailableColorsMetafieldStale && colorJson ? JSON.parse(colorJson.value) : [];
      const availableColors = [
        // preserve the 1st available color to ensure the colorway product's (added to PLP) data matches 1st swatch
        // ex: title: "The Triumph Crew Neck Tee - Black" but we would be showing the wrong swatch color
        ...unsortedAvailableColors.slice(0, 1),
        ...orderBy(unsortedAvailableColors.slice(1), 'sortScore', 'desc'),
      ];
      const hasColors = !isEmpty(availableColors);
      // availableColors[0] to make TS happy; unnecessary this w/ later versions of TS
      if (availableColors[0] && hasColors) this.initialColor = availableColors[0].name;
      return excludeSoldOut && hasColors
        ? availableColors.filter((color) => color.totalInventory > 0)
        : availableColors;
    },

    get computedData() {
      const productData = this.productData;
      const isCurveCollection = (collectionHandle && collectionHandle === 'curve') || false;
      const { curveIndicators } = getCurveImages();

      const curveImages = productData.images.edges.filter((edge) => {
        const sizeCode = getProductImageParts(edge?.node?.originalSrc)?.sizeCode;
        return sizeCode && curveIndicators.includes(sizeCode);
      });

      const productImages: ImagesEdge[] = isCurveCollection ? curveImages : productData.images.edges;
      //fixes TS assumption about indexing in arrays;
      const { options, productType } = productData;

      const availableColors = this.availableColors;
      const colorData = availableColors?.find(
        (colorOption, idx) =>
          idx === this.activeSwatchIndex &&
          // colorbot shouldn't have entries for "bad colors"
          !lowerCasedBlacklistedOptionValues.includes(colorOption.name.toLowerCase().replaceAll(' ', ''))
      );
      const optionsForColorProduct = find(options, {
        name: 'Size',
      });

      const activeInventory = colorData?.totalInventory;
      const title =
        this.initialColor && colorData
          ? productData.title.replace(`- ${this.initialColor}`, `- ${colorData.name}`)
          : productData.title;
      const inventoryBySize = colorData?.inventoryBySize;
      const colorName = colorData?.name;
      const baseTitle = title.replace(` - ${colorName}`, '');

      let needsUrgencyBadge: boolean =
        activeInventory && activeInventory > 0
          ? processBadgeUrgency(inventoryBySize, optionsForColorProduct?.values)
          : false;

      // in the absence of colorData should we default to `[]` or landed product tags?
      const tags = this.activeSwatchIndex === 0 ? productData.tags : colorData?.tags ?? productData.tags;

      // ? [REVISIT] rewrite everything below w/ optional chaining
      return {
        handle: (colorData && colorData.handle) || productData.handle,
        id: (colorData && last(colorData.id.split('/'))) || '',
        tags,
        productType,
        isCurveCollection,
        // ? [REVISIT] adding the `_1x` seems hacky, remove the graphql resp
        primaryImage:
          isCurveCollection && colorData?.curveProductImage
            ? first([colorData.curveProductImage.replace('.jpg', '_1x.jpg')])
            : first(
                colorData && colorData.productImage
                  ? // ? [REVISIT]
                    [colorData.productImage.replace('.jpg', '_1x.jpg')]
                  : productImages.map((item) => item.node.originalSrc)
              ),
        needsUrgencyBadge: needsUrgencyBadge,
        secondaryImage:
          isCurveCollection && colorData?.secondCurveProductImage
            ? first([colorData.secondCurveProductImage.replace('.jpg', '_1x.jpg')])
            : first(
                colorData && colorData.productImage
                  ? // ? [REVISIT]
                    [colorData.secondaryImage ? colorData.secondaryImage.replace('.jpg', '_1x.jpg') : null]
                  : productImages.slice(1, 2).map((item) => item.node.originalSrc)
              ),
        href: `/products/${(colorData && colorData.handle) || productData.handle}`,
        title,
        baseTitle,
        colorName,
        // ? [REVISIT] default assuming false in the absence of data; Vendor for Simples & Set are always 'true'?
        availableForSale:
          productData.vendor === 'Alo Yoga Simple' || productData.vendor === 'Alo Yoga Set'
            ? true
            : this.activeSwatchIndex === 0
            ? productData.availableForSale
            : colorData
            ? colorData.totalInventory > 0
            : false,
      };
    },
    setActiveSwatchIndex(value: number) {
      this.hasColorChanged = true;
      this.activeSwatchIndex = value;
    },
  };
  return makeAutoObservable(schemaConfig, { productData: observable.ref });
}

// ? in practice does `compareAtPrice` & `maxPrice` differ? where?
export function getDiscountBadgeCopy(
  compareAtPrice: number | undefined,
  minPrice: number,
  maxPrice: number | undefined
) {
  const delta = maxPrice ? maxPrice - minPrice : 0;
  const discountBadgeCopy =
    compareAtPrice && minPrice > 0
      ? delta > 0
        ? processBadgeDiscount(compareAtPrice, minPrice, maxPrice)
        : processBadgeDiscount(compareAtPrice, minPrice)
      : undefined;

  return discountBadgeCopy;
}
